/**
 * RatingBadge - Read-only pill showing an inspection item's rating tier.
 *
 * One badge, used by the verify step, the detail page, the inspection list,
 * and the dashboard, so a "Critical" reads identically everywhere. Colors and
 * wording come from utils/inspectionRating, which mirrors the backend's
 * vocabulary — no surface carries its own copy.
 *
 * @example
 * <RatingBadge rating="critical" />
 * <RatingBadge rating={item.rating} showAction />
 */
import React from 'react';
import { ratingMeta } from '../../../utils/inspectionRating';

export interface RatingBadgeProps {
	/** The tier — 'good' | 'needs_attention' | 'critical', or null when unrated. */
	rating: string | null | undefined;
	/** Also render the action the tier calls for, beneath the badge. */
	showAction?: boolean;
	/** Compact variant for dense table rows. */
	size?: 'sm' | 'md';
	/** Optional extra classes merged onto the root. */
	className?: string;
}

/** Size presets, written out in full so Tailwind's scanner sees them. */
const SIZE_CLASSES: Record<NonNullable<RatingBadgeProps['size']>, string> = {
	sm: 'px-8px py-1px text-10px',
	md: 'px-10px py-2px text-12px',
};

export const RatingBadge: React.FC<RatingBadgeProps> = ({
	rating,
	showAction = false,
	size = 'md',
	className = '',
}) => {
	const meta = ratingMeta(rating);

	// Unrated is a real state on an in-progress inspection, so it gets a
	// neutral badge rather than rendering nothing (which would read as a bug).
	const classes = meta ? meta.badgeClasses : 'bg-gray-100 text-gray-600 border-gray-300';
	const label = meta ? meta.label : 'Not rated';

	return (
		<span className={`rating-badge rating-badge--${meta?.value ?? 'unrated'} inline-flex flex-col ${className}`} data-ref="atom-rating-badge">
			<span className={`rating-badge__label inline-flex items-center rounded-full border font-sans font-semibold uppercase tracking-wide whitespace-nowrap ${SIZE_CLASSES[size]} ${classes}`}>
				{label}
			</span>
			{showAction && meta && (
				<span className="rating-badge__action mt-4px text-12px font-sans text-brand-text-muted">
					{meta.action}
				</span>
			)}
		</span>
	);
};

export default RatingBadge;
